import { LogoutOptions } from '@auth0/auth0-spa-js';
import { useCallback, useMemo } from 'react';
import { useAuth0 } from 'services/auth0/react-auth0.spa';
import { debugLog } from 'shared/logger/debugLog';
import { getUiStoreState } from 'store/ui';
import { getUserStoreState } from 'store/user';
import { channelBus } from 'shared/channels';
import { requestServiceWorkerLogout } from 'services/auth/requestServiceWorkerLogout';
import { markLogoutFlowStarted } from './model';
import {
  clearLocalStorageOnLogout,
  performLogoutLocalCleanup,
} from './logoutLocalCleanup';
import { LogoutActions } from './types';

interface LogoutSequence {
  clearLocalStorage: boolean;
  /** False when the worker already signed out and told this tab. */
  notifyServiceWorker: boolean;
  options?: LogoutOptions;
}

async function runLogoutSequence(
  auth0Logout: (options?: LogoutOptions) => void | Promise<void>,
  { clearLocalStorage, notifyServiceWorker, options }: LogoutSequence
): Promise<void> {
  markLogoutFlowStarted();

  try {
    debugLog('useLogout: logout sequence started', { clearLocalStorage, notifyServiceWorker });
    performLogoutLocalCleanup(clearLocalStorage);

    if (notifyServiceWorker) {
      await requestServiceWorkerLogout();
    }
    // The signed-out page hears nothing more once it leaves for Auth0; if
    // Auth0 does not navigate away, the tab keeps working.
    window.addEventListener('pagehide', () => channelBus.closeAll(), { once: true });

    await Promise.resolve(auth0Logout(options));
  } catch (error) {
    debugLog('useLogout: logout sequence failed', error);
  } finally {
    clearLocalStorageOnLogout();
    getUiStoreState().actions.reset();
    getUserStoreState().actions.reset();
    debugLog('useLogout: app state reset');
  }
}

/** Signing out of this tab. The "logout in progress" flag lives in `./model`, so it is shared by every caller. */
export function useLogout(): LogoutActions {
  const { loading, logout: auth0Logout } = useAuth0();

  const logout = useCallback((options?: LogoutOptions): void => {
    void runLogoutSequence(auth0Logout, { clearLocalStorage: false, notifyServiceWorker: true, options });
  }, [auth0Logout]);

  const logoutAndClear = useCallback((options?: LogoutOptions): void => {
    void runLogoutSequence(auth0Logout, { clearLocalStorage: true, notifyServiceWorker: true, options });
  }, [auth0Logout]);

  const logoutLocally = useCallback((): void => {
    void runLogoutSequence(auth0Logout, { clearLocalStorage: false, notifyServiceWorker: false });
  }, [auth0Logout]);

  return useMemo(
    () => ({ logout, logoutAndClear, logoutLocally, loading }),
    [logout, logoutAndClear, logoutLocally, loading]
  );
}
